import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Elements, RecurlyProvider } from '@recurly/react-recurly';
import styled from 'styled-components';
import { Context } from '@components/Context';
import ExpressCheckoutButtons from '@components/shared/ExpressCheckoutButtons';
import { ExpressCheckoutErrorProvider } from '@components/shared/ExpressCheckoutErrorContext';
import { CartItem } from '@contracts';
import { calculateTotalPrice } from '@utils/cart';
import { getFreeGiftsForPromotion } from '@utils/freeGift';
import { loadRecurly } from '@utils/loadScripts';
// @ts-ignore
import loadRecurlyCSS from '../../../recurly';
import { useShoppingContext } from './ProductShoppingContext';

const ProductExpressBuy: React.FC = () => {
  const { state } = useContext(Context);
  const { country, currency } = state;
  const { selectedCartItems } = useShoppingContext();
  const [recurlyLoaded, setRecurlyLoaded] = useState(false);

  const totalPrice = calculateTotalPrice(selectedCartItems);

  const cartItemsWithFreeGifts = useMemo(() => {
    if (!selectedCartItems.length) return [];

    const selectedFreeGifts: CartItem[] = [];

    selectedCartItems.forEach((cartItem) => {
      const freeGifts = getFreeGiftsForPromotion(cartItem, country, currency, totalPrice);
      freeGifts.forEach((gift) => {
        if (!selectedFreeGifts.some((existingGift) => existingGift.id === gift.id)) {
          selectedFreeGifts.push(gift);
        }
      });
    });

    return [...selectedCartItems, ...selectedFreeGifts];
  }, [selectedCartItems, country, currency, totalPrice]);

  useEffect(() => {
    loadRecurlyCSS();
    const checkRecurlyLoaded = async () => {
      await loadRecurly();
      if (window.recurly && window.recurly.Recurly) {
        setRecurlyLoaded(true);
      } else {
        // If not loaded, check again after a delay
        setTimeout(checkRecurlyLoaded, 200);
      }
    };

    checkRecurlyLoaded();
    loadRecurlyCSS();
  }, []);

  if (!recurlyLoaded) {
    return null;
  }

  // Fix for TypeScript errors - use a type assertion to avoid the children property error
  const recurlyProviderProps = {
    publicKey: process.env.RECURLY_KEY || '',
    required: ['cvv']
  } as any;

  return (
    <RecurlyProvider {...recurlyProviderProps}>
      <ExpressCheckoutErrorProvider>
        <Container>
          {selectedCartItems?.length ? (
            // @ts-ignore - Elements component from Recurly doesn't have proper TypeScript definitions
            <Elements>
              <ExpressCheckoutButtons cartItems={cartItemsWithFreeGifts} price={totalPrice} />
            </Elements>
          ) : null}
        </Container>
      </ExpressCheckoutErrorProvider>
    </RecurlyProvider>
  );
};

export default ProductExpressBuy;

const Container = styled.div`
  width: 100%;
  position: relative;
  z-index: 1;

  & > button,
  & > div {
    margin-top: 16px;
    border-radius: 2px;
  }
`;
